/**
 * Canonical continuity traversal — Signal → Preserve → Represent → Witness → Inspect → Replay → Symbol → Horizon.
 * Cross-post is the primary demo anchor; Message Bottle and Call are secondary.
 */

export const CANONICAL_TRAVERSAL_TAGLINE =
  "A human or AI-assisted post becomes a canonical continuity record. Platforms receive representations. XO holds the inspectable source of truth." as const;

export const AETHER_HANDLE_FRAMING = {
  title: "Continuity handle",
  line: "The handle is not the platform account. It is the continuity identity behind platform appearances.",
  binds: [
    "Authored messages and preserved signals",
    "Social representations (X, LinkedIn, Discord, sites)",
    "Verification and witness state",
    "Reputation continuity across time",
    "Human + AI context without platform lock-in",
  ],
  note: "Demo-safe — conceptual binding only. No live finance integrations or token claims.",
} as const;

export const CONTINUITY_ECHO_LINES = [
  "Continuity echoes returning…",
  "Witnessed replies preserved",
  "Signals returning to the record",
] as const;

export const PUBLIC_SURFACE_FALLBACK = {
  title: "Continuity view temporarily unavailable",
  body: "The preserved signal remains intact. You can still inspect the continuity record.",
  chunk: "This view needs a refresh after the latest update — the continuity record remains inspectable.",
} as const;

/** Public replay strip (canonical spine — not call demo). */
export const CANONICAL_TRAVERSAL_PROGRESSION = [
  "Signal",
  "Preserve",
  "Represent",
  "Witness",
  "Inspect",
  "Replay",
  "Call",
] as const;

export type CanonicalTraverseSlug =
  | "signal"
  | "preserve"
  | "represent"
  | "witness"
  | "inspect"
  | "replay"
  | "call"
  | "symbol"
  | "horizon";

export function traverseSlugForCrossPostPhase(
  phase: "compose" | "confirm" | "preserve" | "crosspost" | "verify",
): CanonicalTraverseSlug {
  if (phase === "compose") return "signal";
  if (phase === "confirm" || phase === "preserve") return "preserve";
  if (phase === "crosspost") return "represent";
  return "inspect";
}

export function crossPostPhaseFromTraverseSlug(slug: string | null | undefined): "compose" | "confirm" | "preserve" | "crosspost" | "verify" | null {
  const s = (slug || "").trim().toLowerCase();
  if (!s || s === "signal") return "compose";
  if (s === "preserve") return "confirm";
  if (s === "represent" || s === "crosspost") return "crosspost";
  if (s === "inspect" || s === "verify") return "verify";
  if (s === "witness") return "verify";
  return null;
}
